import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, playerAPI } from '../lib/api';
import { Eye, EyeSlash, GoogleLogo, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { captureInviteCode, clearPendingInviteCode } from '../lib/inviteCode';
import rippersClubLogo from '../assets/rippers-club-logo.png';

const USTA_RATINGS = ['3.0', '3.5', '4.0', '4.5', '5.0'];

// Same hero asset + responsive sizing as LoginPage to keep the
// authentication flow visually identical on signup → login.
const HERO_BASE = 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8';
const HERO_LARGE = `${HERO_BASE}?auto=format&fit=crop&w=2400&q=80`;
const HERO_SMALL = `${HERO_BASE}?auto=format&fit=crop&w=900&q=70`;

/**
 * Reusable hero background + dark overlays + branding cluster.
 * Mirrors LoginPage exactly so the visuals stay consistent.
 */
function ThemedAuthShell({ children }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.src = HERO_LARGE;
    img.onload = () => setImgLoaded(true);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#013523]">
      {/* Fixed hero — stays in place while the form scrolls on small screens. */}
      <picture
        aria-hidden="true"
        className={`fixed inset-0 transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <source media="(min-width: 768px)" srcSet={HERO_LARGE} />
        <img
          src={HERO_SMALL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
        />
      </picture>

      {/* Same multi-stop overlay treatment as the login screen for legibility. */}
      <div aria-hidden="true" className="fixed inset-0 bg-black/55" />
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70"
      />
      <div
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 35%, transparent 0%, rgba(0,0,0,0.35) 80%)',
        }}
      />

      <div className="relative min-h-screen flex flex-col items-center px-5 py-10">
        {/* Branding — full attached logo, no synthetic background. */}
        <div className="flex flex-col items-center mb-6 sm:mb-8" data-testid="onboard-logo">
          <img
            src={rippersClubLogo}
            alt="The The Rippers Club"
            className="w-56 sm:w-64 h-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
          />
        </div>

        {children}
      </div>
    </div>
  );
}

export default function OnboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasLadderAccess, profileCompleted, login, refreshUser } = useAuth();

  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [phone, setPhone] = useState('');
  const [ustaRating, setUstaRating] = useState('');
  const [gender, setGender] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const isAuthenticated = !!user;

  // Capture code on mount and validate against backend
  useEffect(() => {
    const captured = captureInviteCode(location.search);
    if (!captured) {
      setValidating(false);
      setInviteError('No invite code found. Ask the ladder administrator to share your invite link.');
      return;
    }
    setCode(captured);
    (async () => {
      try {
        const res = await authAPI.validateInvite(captured);
        if (res.data.valid) {
          setInviteValid(true);
          if (res.data.email) setEmail(res.data.email);
          if (res.data.full_name) {
            const parts = res.data.full_name.trim().split(/\s+/);
            setFirstName(parts[0] || '');
            setLastName(parts.slice(1).join(' ') || '');
          }
        } else {
          setInviteError(res.data.reason || 'Invite is not valid.');
        }
      } catch (e) {
        setInviteError('Could not verify invite code. Please try again.');
      } finally {
        setValidating(false);
      }
    })();
  }, [location.search]);

  // Pre-fill from authenticated user metadata when OAuth brought them here
  useEffect(() => {
    if (user && !firstName && !lastName) {
      const fn = user.first_name || '';
      const ln = user.last_name || '';
      if (fn || ln) {
        setFirstName(fn);
        setLastName(ln);
      } else if (user.name) {
        const parts = user.name.trim().split(/\s+/);
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' ') || '');
      }
      if (user.email && !email) setEmail(user.email);
    }
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (user && hasLadderAccess && profileCompleted) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError('');

    if (!firstName.trim() || !lastName.trim()) {
      setFormError('First and last name are required.');
      return;
    }
    if (!isAuthenticated && (!email.trim() || !password)) {
      setFormError('Email and password are required.');
      return;
    }

    setLoading(true);
    try {
      // Two flows depending on entry point:
      //  (a) OAuth pre-auth (Google) — user is already signed in and just
      //      needs to redeem the invite + complete their profile.
      //  (b) Standard invite completion — atomic /auth/complete-invite call
      //      reconciles any pre-existing admin-created profile / auth user
      //      so we never fail with "Email already registered".
      if (isAuthenticated) {
        if (!hasLadderAccess) {
          await authAPI.redeemInvite(code);
        }
        await playerAPI.updateProfile({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim() || null,
          usta_rating: ustaRating || null,
          gender: gender || null,
          email_opt_in: emailOptIn,
          sms_opt_in: smsOptIn,
        });
      } else {
        const res = await authAPI.completeInvite({
          invite_code: code,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim() || null,
          usta_rating: ustaRating || null,
          gender: gender || null,
          email_opt_in: emailOptIn,
          sms_opt_in: smsOptIn,
        });
        login(
          res.data.user,
          res.data.player,
          res.data.has_ladder_access,
          res.data.profile_completed,
        );
      }

      await refreshUser();
      clearPendingInviteCode();
      toast.success('Welcome to The Rippers Club!');
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setFormError(typeof detail === 'string' ? detail : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while we validate the invite — themed, on the dark hero.
  if (validating) {
    return (
      <ThemedAuthShell>
        <div className="w-full max-w-sm flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
        </div>
      </ThemedAuthShell>
    );
  }

  // Bad invite — themed glass card matching the login look.
  if (!inviteValid) {
    return (
      <ThemedAuthShell>
        <div
          className="w-full max-w-sm rounded-3xl p-6 sm:p-7 space-y-5 backdrop-blur-xl bg-white/10 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.45)] text-center"
          data-testid="onboard-invalid-invite"
        >
          <div className="w-14 h-14 rounded-2xl bg-red-500/20 mx-auto flex items-center justify-center">
            <WarningCircle size={28} weight="duotone" className="text-red-300" />
          </div>
          <h2 className="font-serif text-2xl text-white tracking-tight">Invite unavailable</h2>
          <p className="text-sm text-white/75">{inviteError}</p>
          <div className="space-y-2 pt-1">
            <Link
              to="/redeem-invite"
              className="block w-full bg-[#015e3f] hover:bg-[#014d34] active:scale-[0.99] transition-transform text-white rounded-full px-6 py-3 font-medium shadow-[0_8px_24px_rgba(1,94,63,0.5)]"
              data-testid="onboard-manual-entry-link"
            >
              Enter invite code manually
            </Link>
            <Link
              to="/login"
              className="block w-full bg-white text-[#013523] hover:bg-white/90 active:scale-[0.99] transition-transform rounded-full px-6 py-3 font-medium border border-white/30 shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            >
              Sign in instead
            </Link>
          </div>
        </div>
      </ThemedAuthShell>
    );
  }

  // Shared input className — mirrors LoginPage inputs for visual parity.
  const inputCls =
    'w-full bg-white/15 border border-white/25 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#c2a251]/60 focus:border-[#c2a251]/60 transition-all';
  const labelCls =
    'text-[11px] font-sans tracking-[0.18em] uppercase text-white/70 mb-1.5 block';

  return (
    <ThemedAuthShell>
      <div
        className="w-full max-w-sm rounded-3xl p-6 sm:p-7 space-y-5 backdrop-blur-xl bg-white/10 border border-white/20 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
        data-testid="onboard-page"
      >
        <div>
          <h2 className="font-serif text-2xl text-white tracking-tight">
            {isAuthenticated ? 'Finish your profile' : 'Join The Rippers Club'}
          </h2>
          <p className="text-xs text-white/70 mt-0.5">
            {isAuthenticated
              ? 'Redeem your invite and complete your profile.'
              : 'Create your account to redeem this invite.'}
          </p>
        </div>

        {/* Validated invite badge */}
        <div
          data-testid="onboard-invite-badge"
          className="flex items-center gap-2 bg-[#c2a251]/20 text-[#f5e6b8] text-xs rounded-full px-3 py-2 justify-center border border-[#c2a251]/30"
        >
          <CheckCircle size={14} weight="duotone" />
          <span>Invite verified:</span>
          <span className="font-mono font-semibold tracking-wider" data-testid="onboard-code">{code}</span>
        </div>

        {formError && (
          <div
            data-testid="onboard-error"
            className="bg-red-500/15 text-red-200 text-sm rounded-xl px-4 py-3 border border-red-400/30"
          >
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="onboard-first-name" className={labelCls}>First Name *</label>
              <input
                id="onboard-first-name"
                data-testid="onboard-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputCls}
                placeholder="John"
                required
              />
            </div>
            <div>
              <label htmlFor="onboard-last-name" className={labelCls}>Last Name *</label>
              <input
                id="onboard-last-name"
                data-testid="onboard-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputCls}
                placeholder="Smith"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="onboard-email" className={labelCls}>Email *</label>
            <input
              id="onboard-email"
              data-testid="onboard-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isAuthenticated}
              className={`${inputCls} ${isAuthenticated ? 'opacity-60 cursor-not-allowed' : ''}`}
              placeholder="your@email.com"
              required
            />
          </div>

          {!isAuthenticated && (
            <div>
              <label htmlFor="onboard-password" className={labelCls}>Password *</label>
              <div className="relative">
                <input
                  id="onboard-password"
                  data-testid="onboard-password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputCls} pr-12`}
                  placeholder="Create a password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="onboard-phone" className={labelCls}>Phone</label>
            <input
              id="onboard-phone"
              data-testid="onboard-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputCls}
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label htmlFor="onboard-usta-rating" className={labelCls}>USTA Rating</label>
            <select
              id="onboard-usta-rating"
              data-testid="onboard-usta-rating"
              value={ustaRating}
              onChange={(e) => setUstaRating(e.target.value)}
              className={`${inputCls} appearance-none`}
            >
              <option value="" className="text-[#013523]">Select rating…</option>
              {USTA_RATINGS.map((r) => (
                <option key={r} value={r} className="text-[#013523]">{r}</option>
              ))}
            </select>
          </div>

          {/* Gender — required for Men's / Ladies Night eligibility. Optional
              field; users who skip can set it later in Settings. */}
          <div>
            <label htmlFor="onboard-gender" className={labelCls}>Gender</label>
            <select
              id="onboard-gender"
              data-testid="onboard-gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className={`${inputCls} appearance-none`}
            >
              <option value="" className="text-[#013523]">Select…</option>
              <option value="male" className="text-[#013523]">Male</option>
              <option value="female" className="text-[#013523]">Female</option>
            </select>
            <p className="mt-1 text-[11px] text-white/55">
              Required to register for Men's Night or Ladies Night events.
            </p>
          </div>

          <div className="space-y-3 bg-white/5 rounded-xl border border-white/15 p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="onboard-email-opt-in"
                type="checkbox"
                checked={emailOptIn}
                onChange={(e) => setEmailOptIn(e.target.checked)}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#c2a251] focus:ring-[#c2a251]/30"
              />
              <span className="text-sm text-white/85">Email notifications</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="onboard-sms-opt-in"
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#c2a251] focus:ring-[#c2a251]/30"
              />
              <span className="text-sm text-white/85">SMS notifications</span>
            </label>
            <p
              data-testid="onboard-sms-disclosure"
              className="text-[10px] leading-relaxed text-white/45 pl-7"
            >
              By providing your phone number and enabling SMS notifications,
              you agree to receive text messages related to your ladder
              activity, including challenges, match updates, and invites.
              Message frequency varies. Message and data rates may apply. Reply
              STOP to opt out.
            </p>
          </div>

          <button
            data-testid="onboard-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#015e3f] hover:bg-[#014d34] active:scale-[0.99] transition-transform text-white rounded-full px-6 py-3 font-medium shadow-[0_8px_24px_rgba(1,94,63,0.5)] disabled:opacity-50"
          >
            {loading ? 'Joining…' : (isAuthenticated ? 'Join The Rippers Club' : 'Create Account & Join')}
          </button>
        </form>

        {!isAuthenticated && (
          <>
            {/* OR divider — same as login. */}
            <div className="flex items-center gap-3" aria-hidden="true">
              <div className="flex-1 h-px bg-white/20" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/55">or</span>
              <div className="flex-1 h-px bg-white/20" />
            </div>

            <button
              data-testid="onboard-google-button"
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-2 bg-white text-[#013523] hover:bg-white/90 active:scale-[0.99] transition-transform rounded-full px-6 py-3 font-medium border border-white/30 shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            >
              <GoogleLogo size={20} weight="bold" />
              Continue with Google
            </button>

            <p className="text-center text-sm text-white/70">
              Already have an account?{' '}
              <Link
                to={`/login?code=${code}`}
                className="text-white font-medium hover:underline"
              >
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>

      <p className="mt-6 text-[11px] text-white/55 text-center max-w-sm leading-relaxed">
        Account approval required. Use the invite code your administrator sent you, or{' '}
        <Link to="/join" className="text-white hover:underline">request access</Link>.
      </p>

      <div className="mt-4 flex items-center gap-3 text-[11px] text-white/60">
        <Link to="/privacy" className="hover:text-white/90" data-testid="onboard-privacy-link">
          Privacy Policy
        </Link>
        <span aria-hidden="true" className="text-white/30">•</span>
        <Link to="/terms" className="hover:text-white/90" data-testid="onboard-terms-link">
          Terms
        </Link>
        <span aria-hidden="true" className="text-white/30">•</span>
        <Link to="/sms-terms" className="hover:text-white/90" data-testid="onboard-sms-terms-link">
          SMS Terms
        </Link>
      </div>
    </ThemedAuthShell>
  );
}
